import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { IFilterWithValues } from "../../List/components/FilterModal";
import { getDimensionForFilterKey } from "../utils/segmentFilterMap";

interface ActiveFilterChipsProps {
	filters: IFilterWithValues;
	onRemove: (filterKey: string) => void;
	onClearAll: () => void;
}

interface ChipItem {
	key: string;
	dimensionLabel: string;
	valueLabel: string;
}

const extractValueLabel = (value: IFilterWithValues[string]["value"]): string => {
	if (Array.isArray(value)) {
		return value
			.map((entry) => {
				if (entry && typeof entry === "object") {
					if ("title" in entry && typeof entry.title === "string") {
						return entry.title;
					}

					if ("name" in entry && typeof entry.name === "string") {
						return entry.name;
					}
				}

				return "";
			})
			.filter(Boolean)
			.join(", ");
	}

	return "";
};

export const ActiveFilterChips: FunctionComponent<ActiveFilterChipsProps> = ({
	filters,
	onRemove,
	onClearAll,
}) => {
	const { t } = useTranslation();

	const items: ChipItem[] = useMemo(() => {
		return Object.entries(filters)
			.map(([key, filter]) => {
				const dimension = getDimensionForFilterKey(key);
				const dimensionLabel = dimension ? t(`analytics.filterDimension.${dimension}`) : key;
				const valueLabel = extractValueLabel(filter.value);

				return { key, dimensionLabel, valueLabel };
			})
			.filter((item) => item.valueLabel.length > 0);
	}, [filters, t]);

	if (items.length === 0) {
		return null;
	}

	return (
		<div
			aria-label={t("analytics.activeFilters")}
			className="flex flex-row flex-wrap items-center gap-2 w-full max-w-5xl mb-3"
			role="group"
		>
			<span className="text-xs text-text-meta uppercase tracking-wide">
				{t("analytics.activeFilters")}
			</span>

			{items.map((item) => (
				<span
					key={item.key}
					className="inline-flex items-center gap-1.5 bg-blue-action-200 text-blue-action text-sm rounded-xl pl-3 pr-1 py-1"
				>
					<span className="font-medium">{item.dimensionLabel}:</span>
					<span>{item.valueLabel}</span>
					<button
						aria-label={`${t("analytics.clearAllFilters")} ${item.dimensionLabel}`}
						className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-blue-action hover:text-brand-white transition-colors cursor-pointer"
						type="button"
						onClick={() => onRemove(item.key)}
					>
						<Icon className="w-3 h-3" icon={faXmark} />
					</button>
				</span>
			))}

			{items.length > 1 ? (
				<button
					className="text-sm text-text-meta hover:text-text underline underline-offset-2 cursor-pointer"
					type="button"
					onClick={onClearAll}
				>
					{t("analytics.clearAllFilters")}
				</button>
			) : null}
		</div>
	);
};
